import { API_BASE_URL } from "@/config/api";
import { useAuth } from "@/contexts/AuthContext";
import { getAuthHeaders, getJsonAuthHeaders } from "@/lib/apiHeaders";
import type { WeeklyReport } from "@/types";
import { useEffect, useState } from "react";

export const useReports = (email?: string) => {
  const [reports, setReports] = useState<WeeklyReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    const fetchReports = async () => {
      try {
        console.log("useReports: Starting to fetch reports");
        const userEmail = email || user?.email;

        console.log("useReports: User email:", userEmail);
        console.log("useReports: User from AuthContext:", user);
        console.log("useReports: User role:", user?.role);

        // For admin users, we can fetch all reports without requiring a specific email
        if (!userEmail && user?.role !== "admin") {
          throw new Error("No user email found");
        }

        // If user is admin, fetch all reports via admin endpoint, otherwise fetch user-specific reports
        const apiUrl =
          user?.role === "admin"
            ? `${API_BASE_URL}/admin/reports`
            : `${API_BASE_URL}/reports?email=${userEmail}`;

        console.log("useReports: Fetching from:", apiUrl);

        const response = await fetch(apiUrl, {
          headers: getAuthHeaders(),
        });
        console.log("useReports: Response status:", response.status);

        if (!response.ok) {
          throw new Error("Failed to fetch reports");
        }

        const data = await response.json();
        console.log("useReports: Received data length:", data.length);
        console.log("useReports: First few reports:", data.slice(0, 3));

        setReports(data);
      } catch (err) {
        console.error("useReports: Error fetching reports:", err);
        setError(err instanceof Error ? err : new Error("Unknown error"));
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, [email, user]);

  const createReport = async (
    report: Omit<WeeklyReport, "id" | "createdAt">,
  ) => {
    try {
      const newReport = {
        ...report,
        id: crypto.randomUUID(),
        createdAt: new Date().toISOString(),
      };

      const response = await fetch(`${API_BASE_URL}/reports`, {
        method: "POST",
        headers: getJsonAuthHeaders(),
        body: JSON.stringify(newReport),
      });

      if (!response.ok) {
        throw new Error("Failed to create report");
      }

      // Refresh reports after creating
      const userEmail = email || user?.email;

      if (!userEmail && user?.role !== "admin") {
        throw new Error("No user email found");
      }

      const refreshUrl =
        user?.role === "admin"
          ? `${API_BASE_URL}/reports?email=${userEmail || "admin"}`
          : `${API_BASE_URL}/reports?email=${userEmail}`;

      const refreshResponse = await fetch(refreshUrl, {
        headers: getAuthHeaders(),
      });
      if (refreshResponse.ok) {
        const data = await refreshResponse.json();
        setReports(data);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error("Failed to create report"),
      );
      throw err;
    }
  };

  return { reports, loading, error, createReport };
};
